import React, { useCallback, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { Button } from '@picsio/ui';

import {
  clearAggregatedProductDetails,
  fetchAggregatedProductDetails,
} from '../../../store/products/actions';
import {
  selectAggregatedDetailsError,
  selectAggregatedDetailsStatus,
  selectAggregatedPanelTitle,
} from '../../../store/selectors/aggregatedProductDetailsSelectors';
import {
  selectProductSelection,
} from '../../../store/selectors/productSelectionSelectors';
import { isMasterSelectionContext } from '../../../store/products/helpers/selectionContext';
import localization from '../../../shared/strings';
import ProductInfoSkeleton from '../ProductInfoSkeleton';
import tabStyles from '../ProductStateTabs/styles.module.scss';
import panelStyles from '../styles.module.scss';
import AggregatedProductDetails from './AggregatedProductDetails';
import AggregatedBulkAttributesNote from './sections/AggregatedBulkAttributesNote';
import styles from './styles.module.scss';

const selectionSnapshotKey = (selection) => {
  const contextKey = selection.context?.type === 'master'
    ? 'master'
    : `${selection.context?.channelId}:${selection.context?.recordState}`;
  if (selection.isComplete) return `${contextKey}:all`;
  const { keys } = selection;
  return `${contextKey}:${keys.length}:${keys[0] || ''}:${keys[keys.length - 1] || ''}`;
};

const aggregatedL10n = localization.PRODUCT_INFO.aggregated;
const panelClassName = cn('InfoPanel', panelStyles.panel);

const AggregatedProductInfo = () => {
  const dispatch = useDispatch();
  const selection = useSelector(selectProductSelection);
  const status = useSelector(selectAggregatedDetailsStatus);
  const error = useSelector(selectAggregatedDetailsError);
  const panelTitle = useSelector(selectAggregatedPanelTitle);
  const isMasterContext = isMasterSelectionContext(selection.context);
  const snapshotKey = useMemo(() => selectionSnapshotKey(selection), [selection]);

  useEffect(() => {
    const promise = dispatch(fetchAggregatedProductDetails());
    return () => {
      promise.abort();
      dispatch(clearAggregatedProductDetails());
    };
  }, [dispatch, snapshotKey]);

  const handleRetry = useCallback(() => {
    dispatch(fetchAggregatedProductDetails());
  }, [dispatch]);

  if (status === 'loading' || status === 'idle') {
    return (
      <div className={cn(panelClassName, panelStyles.skeletonHost)}>
        <ProductInfoSkeleton />
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className={panelClassName}>
        <div className="detailsPanel__empty">
          <div className="txtNoImageSelected">{aggregatedL10n.errorTitle}</div>
          <If condition={error}>
            <div className={styles.errorMessage}>{error}</div>
          </If>
          <Button color="primary" onClick={handleRetry}>
            {aggregatedL10n.retry}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={panelClassName}>
      <If condition={panelTitle}>
        <div className={tabStyles.actionsRow}>
          <span className={tabStyles.actionsRowTexts}>{panelTitle}</span>
        </div>
      </If>
      <If condition={isMasterContext}>
        <AggregatedBulkAttributesNote />
      </If>
      <AggregatedProductDetails />
    </div>
  );
};

export default AggregatedProductInfo;
